import { logger } from "../../utils/logger";
import { Sparkles, ArrowRight, Play, Clock, CheckCircle, Award } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '../ui/button';
import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { getApiUrl } from '../../utils/apiConfig';
import * as LucideIcons from 'lucide-react';

interface TrustIndicator {
  id: string;
  icon: string;
  text: string;
  order: number;
}

interface ProcessHeroData {
  id: string;
  badgeText: string;
  badgeIcon: string;
  titleLine1: string;
  titleLine2: string;
  description: string;
  trustIndicators: TrustIndicator[];
  ctaPrimaryText: string;
  ctaPrimaryLink: string;
  ctaPrimaryVisible: boolean;
  ctaSecondaryText: string;
  ctaSecondaryVideoUrl: string;
  ctaSecondaryVisible: boolean;
  visible: boolean;
}

export function ProcessHeroProfessional() {
  const [showVideo, setShowVideo] = useState(false);
  const [heroData, setHeroData] = useState<ProcessHeroData | null>(null);

  useEffect(() => {
    const fetchHeroData = async () => {
      try {

        const response = await fetch(getApiUrl('process-page/hero'));
        
        if (response.ok) {
          const result = await response.json();

          if (result.success && result.data) {
            // Transform backend format to frontend format
            const backendData = result.data;
            const transformedData: ProcessHeroData = {
              id: backendData.id || 'process-hero',
              badgeText: backendData.badge || backendData.badgeText || 'Our Proven 6-Step Process',
              badgeIcon: backendData.badgeIcon || 'Sparkles',
              titleLine1: backendData.title || backendData.titleLine1 || 'Transform Complex Ideas Into Clear,',
              titleLine2: backendData.highlightedTitle || backendData.titleLine2 || 'Engaging Animations',
              description: backendData.description || 'From strategy to delivery, our structured methodology ensures precision, collaboration, and measurable results. Every step revolves around your success.',
              trustIndicators: backendData.trustIndicators || [],
              ctaPrimaryText: backendData.primaryCTA?.text || backendData.ctaPrimaryText || 'Start Your Project',
              ctaPrimaryLink: backendData.primaryCTA?.link || backendData.ctaPrimaryLink || '/contact',
              ctaPrimaryVisible: backendData.primaryCTA?.visible ?? backendData.ctaPrimaryVisible ?? true,
              ctaSecondaryText: backendData.secondaryCTA?.text || backendData.ctaSecondaryText || 'Watch Process Video',
              ctaSecondaryVideoUrl: backendData.secondaryCTA?.videoUrl || backendData.ctaSecondaryVideoUrl || '',
              ctaSecondaryVisible: backendData.secondaryCTA?.visible ?? backendData.ctaSecondaryVisible ?? true,
              visible: backendData.visible ?? true
            };

            setHeroData(transformedData);
          } else {
            logger.warn('⚠️ [ProcessHeroProfessional] No data in response, using defaults');
            setHeroData(null); // Let default merge handle it
          }
        } else {
          logger.error('❌ [ProcessHeroProfessional] Response not OK:', response.status);
          setHeroData(null);
        }
      } catch (error) {
        logger.error('❌ [ProcessHeroProfessional] Error fetching Process hero:', error);
        setHeroData(null);
      }
    };
    
    fetchHeroData();
  }, []);

  // Debug: Log when heroData changes
  useEffect(() => {
    if (heroData) {

    } else {

    }
  }, [heroData]);

  // Fallback data if backend fails
  const defaultData = {
    badgeText: 'Our Proven 6-Step Process',
    badgeIcon: 'Sparkles',
    titleLine1: 'Transform Complex Ideas Into Clear,',
    titleLine2: 'Engaging Animations',
    description: 'From strategy to delivery, our structured methodology ensures precision, collaboration, and measurable results. Every step revolves around your success.',
    trustIndicators: [
      { id: '1', icon: 'Clock', text: '4-8 Week Timeline', order: 1 },
      { id: '2', icon: 'CheckCircle', text: '650+ Projects Delivered', order: 2 },
      { id: '3', icon: 'Award', text: '98% Satisfaction Rate', order: 3 }
    ],
    ctaPrimaryText: 'Start Your Project',
    ctaPrimaryLink: '/contact',
    ctaPrimaryVisible: true,
    ctaSecondaryText: 'Watch Process Video',
    ctaSecondaryVideoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1',
    ctaSecondaryVisible: true,
    visible: true
  };

  // Smart merge: CMS data overrides defaults field-by-field
  // This allows partial CMS data - missing fields fall back to defaults
  const data = heroData ? { ...defaultData, ...heroData } : defaultData;
  
  logger.log('🎨 [ProcessHeroProfessional] Final merged data:', {
    badgeText: data.badgeText,
    badgeIcon: data.badgeIcon,
    titleLine1: data.titleLine1,
    titleLine2: data.titleLine2,
    description: data.description?.substring(0, 50) + '...',
    trustIndicatorsCount: data.trustIndicators?.length,
    ctaPrimaryText: data.ctaPrimaryText,
    ctaPrimaryLink: data.ctaPrimaryLink,
    ctaSecondaryText: data.ctaSecondaryText
  });
  
  // Get icon component dynamically
  const getIconComponent = (iconName: string) => {
    return (LucideIcons as any)[iconName] || Sparkles;
  };

  const BadgeIcon = getIconComponent(data.badgeIcon);

  // Don't render if hidden
  if (!data.visible) {
    return null;
  }

  return (
    <>
      {/* Hero Section - Seamless Universe Theme Integration */}
      <section className="relative py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* ✅ REMOVED duplicate backgrounds - using parent page background instead */}
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            {/* Premium Frosted Glass Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/20 shadow-lg">
              <BadgeIcon className="w-4 h-4 text-[#E8B84D]" />
              <span className="text-sm font-semibold text-white" style={{ textShadow: '0 0 20px rgba(232, 184, 77, 0.8)' }}>{data.badgeText}</span>
            </div>
            
            <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-6 leading-tight text-white" style={{ textShadow: '0 2px 20px rgba(0, 0, 0, 0.3)' }}>
              {data.titleLine1}<br />
              <span className="bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent" style={{ textShadow: 'none' }}>{data.titleLine2}</span>
            </h1>
            
            <p className="text-base sm:text-lg text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed" style={{ textShadow: '0 1px 10px rgba(0, 0, 0, 0.2)' }}>
              {data.description}
            </p>

            {/* Trust Indicators - Premium Cards */}
            <div className="flex items-center justify-center gap-4 sm:gap-6 flex-wrap mb-8">
              {data.trustIndicators.map((item) => {
                const TrustIcon = getIconComponent(item.icon);
                return (
                  <div key={item.id} className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-xl px-4 py-2.5 rounded-full border border-white/20 shadow-lg hover:bg-white/20 hover:shadow-xl hover:border-white/30 transition-all group">
                    <TrustIcon className="w-4 h-4 text-[#E8B84D] group-hover:scale-110 transition-transform" />
                    <span className="text-sm text-white font-medium">{item.text}</span>
                  </div>
                );
              })}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {data.ctaPrimaryVisible && (
                <Link to={data.ctaPrimaryLink}>
                  <Button className="group bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] hover:from-[#E8B84D] hover:to-[#E87D3E] text-white rounded-full px-8 h-12 gap-2 shadow-lg shadow-[#E87D3E]/30 font-bold">
                    {data.ctaPrimaryText}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              )}
              {data.ctaSecondaryVisible && (
                <Button 
                  onClick={() => setShowVideo(true)}
                  className="bg-white/10 hover:bg-white/20 text-white border-2 border-white/30 hover:border-white/40 backdrop-blur-xl rounded-full px-8 h-12 gap-2 shadow-lg font-bold transition-all"
                >
                  <Play className="w-4 h-4" />
                  {data.ctaSecondaryText}
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {showVideo && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowVideo(false)}
        >
          <div 
            className="relative w-full max-w-4xl bg-white rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowVideo(false)}
              className="absolute top-4 right-4 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center z-10 transition-colors"
            >
              ✕
            </button>
            <div className="relative pt-[56.25%]">
              <iframe
                src={data.ctaSecondaryVideoUrl}
                title="Our Process Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}